import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import type { Order } from '@/api/orders/orders-types'
import type { SkidListItem } from '@/api/skids/skids-types'
import { collectSkidsFromOrders } from '@/utils/order-skids'

function extractOrdersFromQueryData(data: unknown): Order[] {
  if (!data || typeof data !== 'object') return []
  const maybePages = (data as { pages?: unknown }).pages
  if (Array.isArray(maybePages)) {
    const out: Order[] = []
    for (const page of maybePages) {
      const results = (page as { results?: Order[] })?.results
      if (Array.isArray(results)) out.push(...results)
    }
    return out
  }
  const results = (data as { results?: Order[] }).results
  if (Array.isArray(results)) return results
  if ('details' in (data as Record<string, unknown>)) return [data as Order]
  return []
}

/**
 * Collect SKI/LHB from every cached `['orders', ...]` / `['order', id]` query. Subscribes to the
 * query cache so components re-render when orders refresh — without issuing a separate `GET /skids/`.
 */
export function useCachedOrdersSkids(): SkidListItem[] {
  const queryClient = useQueryClient()
  const [version, setVersion] = useState(0)

  useEffect(() => {
    const unsub = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return
      const key = event.query.queryKey[0]
      if (key === 'orders' || key === 'order') setVersion((v) => v + 1)
    })
    return unsub
  }, [queryClient])

  return useMemo(() => {
    const orders: Order[] = []
    for (const [, data] of queryClient.getQueriesData({ queryKey: ['orders'] })) {
      orders.push(...extractOrdersFromQueryData(data))
    }
    for (const [, data] of queryClient.getQueriesData({ queryKey: ['order'] })) {
      orders.push(...extractOrdersFromQueryData(data))
    }
    return collectSkidsFromOrders(orders)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, version])
}
